import React, { useEffect, useMemo, useState } from 'react';
import { axiosInstance } from '../../App';
import { toast } from 'sonner';
import {
  X, Eye, FileText, Hash, Calendar, Tag as TagIcon, Link as LinkIcon,
  Edit3, Save, Loader2, ArrowRight, Sparkles,
} from 'lucide-react';
import DocExtractionPanel from './DocExtractionPanel';
import DocApprovalPanel from './DocApprovalPanel';


const KIND_LABEL_AR = {
  supplier_invoice: 'فاتورَة مورِد',
  customer_invoice: 'فاتورَة عَميل',
  receipt_voucher:  'سَنَد قَبض',
  payment_voucher:  'سَنَد صَرف',
  bank_statement:   'كَشف بَنكي',
  contract:         'عَقد',
  purchase_order:   'أَمر شِراء',
  delivery_note:    'سَنَد تَسليم',
  credit_note:      'إشعار دائِن',
  debit_note:       'إشعار مَدين',
  expense_receipt:  'فاتورَة مَصروف',
  attachment:       'مُرفَق',
  other:            'أُخرى',
};

const NEXT_STATES = {
  received:   ['classified', 'rejected'],
  classified: ['extracted', 'rejected'],
  extracted:  ['reviewed',  'rejected'],
  reviewed:   ['approved',  'rejected'],
  approved:   ['posted'],
  posted:     ['paid'],
  paid:       ['archived'],
};

const STATE_AR = {
  received: 'مُستَلَم', classified: 'مُصَنَّف', extracted: 'مُستَخرَج',
  reviewed: 'مُراجَع', approved: 'مُعتَمَد', posted: 'مُرَحَّل',
  paid: 'مَدفوع', archived: 'مُؤَرشَف', rejected: 'مَرفوض',
};


export default function DocPreviewDrawer({ doc: initial, currentUser, onClose, onMutated }) {
  const [doc, setDoc] = useState(initial);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({});
  const [busy, setBusy] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [tab, setTab] = useState('info');  // 'info' | 'extraction' | 'approval'

  // Build a blob URL for inline preview
  useEffect(() => {
    let revoked = null;
    const fetchPreview = async () => {
      try {
        const r = await axiosInstance.get(
          `/document-hub/${doc.id}/preview`, { responseType: 'blob' });
        const url = URL.createObjectURL(r.data);
        setPreviewUrl(url);
        revoked = url;
      } catch { /* silent */ }
    };
    if (doc?.id) fetchPreview();
    return () => { if (revoked) URL.revokeObjectURL(revoked); };
  }, [doc?.id]);

  // Refresh doc from server (after transitions etc.)
  const refresh = async () => {
    try {
      const { data } = await axiosInstance.get(`/document-hub/${doc.id}`);
      setDoc(data);
    } catch { /* silent */ }
  };

  const startEdit = () => {
    setForm({
      title: doc.title || '', description: doc.description || '',
      reference_no: doc.reference_no || '',
      related_party_name: doc.related_party_name || '',
      document_date: doc.document_date || '',
      amount: doc.amount ?? '', currency: doc.currency || 'SAR',
    });
    setEditing(true);
  };

  const save = async () => {
    setBusy(true);
    try {
      const payload = { ...form, reason: 'تَعديل البَيانات' };
      if (payload.amount === '') delete payload.amount;
      const { data } = await axiosInstance.patch(`/document-hub/${doc.id}`, payload);
      setDoc(data.document);
      setEditing(false);
      toast.success('تَم الحِفظ');
      onMutated?.();
    } catch (e) { toast.error(e?.response?.data?.detail || 'فَشَل الحِفظ'); }
    finally { setBusy(false); }
  };

  const transition = async (to_state) => {
    setBusy(true);
    try {
      await axiosInstance.post(`/document-hub/${doc.id}/transition`,
        { to_state, reason: `انتِقال إلى ${STATE_AR[to_state]}` });
      toast.success(`الحالَة الجَديدَة: ${STATE_AR[to_state]}`);
      await refresh();
      onMutated?.();
    } catch (e) { toast.error(e?.response?.data?.detail || 'فَشَل'); }
    finally { setBusy(false); }
  };

  const nextStates = NEXT_STATES[doc.status] || [];
  const isPdf   = (doc.mime_type || '').includes('pdf');
  const isImage = (doc.mime_type || '').startsWith('image/');

  return (
    <div className="fixed inset-0 z-50 flex" data-testid="preview-drawer">
      <div className="flex-1 bg-black/40" onClick={onClose} />
      <div className="w-full max-w-5xl bg-white shadow-2xl h-full flex flex-col" dir="rtl">
        <div className="flex items-center justify-between px-4 py-3 border-b">
          <div className="min-w-0">
            <div className="text-base font-bold truncate">{doc.title || doc.file_name}</div>
            <div className="text-[10px] text-slate-500 font-mono">{doc.file_name}</div>
          </div>
          <button onClick={onClose} data-testid="preview-close" className="p-2 hover:bg-slate-100 rounded-lg">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="flex-1 grid grid-cols-1 md:grid-cols-[1fr,360px] overflow-hidden">
          {/* Preview pane */}
          <div className="bg-slate-100 overflow-auto p-4 flex items-center justify-center">
            {!previewUrl ? (
              <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
            ) : isPdf ? (
              <iframe src={previewUrl} title={doc.file_name} className="w-full h-full min-h-[500px] bg-white rounded shadow"
                       data-testid="preview-iframe" />
            ) : isImage ? (
              <img src={previewUrl} alt={doc.file_name} className="max-w-full max-h-full object-contain"
                    data-testid="preview-image" />
            ) : (
              <div className="text-center text-slate-500">
                <FileText className="w-16 h-16 mx-auto mb-2 opacity-50" />
                <div className="text-sm">المُعايَنَة المُضَمَّنَة غَير مَدعومَة لِهَذا النَّوع.</div>
                <a href={previewUrl} download={doc.file_name}
                   className="inline-block mt-2 text-xs font-semibold rounded bg-indigo-600 text-white px-3 py-1.5">
                  تَنزيل
                </a>
              </div>
            )}
          </div>

          {/* Metadata pane */}
          <div className="border-s overflow-y-auto p-4 space-y-3 bg-slate-50/50">
            {/* Tab selector */}
            <div className="flex items-center gap-1 bg-white rounded-lg border p-0.5" data-testid="drawer-tabs">
              <button
                onClick={() => setTab('info')}
                data-testid="tab-info"
                className={`flex-1 text-[11px] font-semibold rounded px-2 py-1 inline-flex items-center justify-center gap-1 ${
                  tab === 'info' ? 'bg-indigo-600 text-white' : 'text-slate-600 hover:bg-slate-50'
                }`}
              >
                <FileText className="w-3 h-3" /> البَيانات
              </button>
              <button
                onClick={() => setTab('extraction')}
                data-testid="tab-extraction"
                className={`flex-1 text-[11px] font-semibold rounded px-2 py-1 inline-flex items-center justify-center gap-1 ${
                  tab === 'extraction' ? 'bg-violet-600 text-white' : 'text-slate-600 hover:bg-slate-50'
                }`}
              >
                <Sparkles className="w-3 h-3" /> الاستِخراج
                {doc.extraction && (
                  <span className="text-[9px] bg-white/30 rounded px-1">✓</span>
                )}
              </button>
              <button
                onClick={() => setTab('approval')}
                data-testid="tab-approval"
                className={`flex-1 text-[11px] font-semibold rounded px-2 py-1 inline-flex items-center justify-center gap-1 ${
                  tab === 'approval' ? 'bg-amber-600 text-white' : 'text-slate-600 hover:bg-slate-50'
                }`}
              >
                <ArrowRight className="w-3 h-3" /> الاعتِماد
                {doc.workflow_status && (
                  <span className="text-[9px] bg-white/30 rounded px-1">
                    {doc.workflow_status === 'APPROVED' ? '✓'
                      : doc.workflow_status === 'REJECTED' ? '✗'
                      : '…'}
                  </span>
                )}
              </button>
            </div>

            {tab === 'extraction' ? (
              <DocExtractionPanel doc={doc} onMutated={async () => { await refresh(); onMutated?.(); }} />
            ) : tab === 'approval' ? (
              <DocApprovalPanel doc={doc} currentUser={currentUser}
                onMutated={async () => { await refresh(); onMutated?.(); }} />
            ) : (
            <>
            <div className="flex items-center justify-between gap-2">
              <span className="text-[10px] uppercase font-semibold text-slate-500">البَيانات</span>
              {!editing ? (
                <button onClick={startEdit} data-testid="edit-btn"
                        className="text-xs font-semibold rounded bg-white border border-slate-300 hover:bg-slate-100 px-2 py-1 inline-flex items-center gap-1">
                  <Edit3 className="w-3 h-3" /> تَعديل
                </button>
              ) : (
                <button onClick={save} disabled={busy} data-testid="save-btn"
                        className="text-xs font-semibold rounded bg-emerald-600 hover:bg-emerald-700 text-white px-2 py-1 inline-flex items-center gap-1 disabled:opacity-50">
                  <Save className="w-3 h-3" /> حِفظ
                </button>
              )}
            </div>

            {!editing ? (
              <div className="space-y-2 text-xs">
                <div><b>النَّوع:</b> {KIND_LABEL_AR[doc.kind] || doc.kind}</div>
                <div><b>الحالَة:</b> <code>{STATE_AR[doc.status] || doc.status}</code></div>
                {doc.reference_no && <div><b>المَرجِع:</b> <span className="font-mono">{doc.reference_no}</span></div>}
                {doc.related_party_name && <div><b>الطَّرف:</b> {doc.related_party_name}</div>}
                {doc.document_date && <div><b>تاريخ المُستَنَد:</b> <span className="font-mono">{doc.document_date}</span></div>}
                {doc.amount != null && (
                  <div><b>المَبلَغ:</b> <span className="font-mono text-emerald-700">{doc.amount} {doc.currency}</span></div>
                )}
                {doc.description && <div><b>الوَصف:</b> {doc.description}</div>}
                <div className="pt-2 border-t">
                  <div className="text-[10px] text-slate-500">رُفِعَ في:</div>
                  <div className="font-mono text-[11px]">{doc.uploaded_at?.slice(0, 16).replace('T', ' ')}</div>
                  <div className="text-[10px] text-slate-500 mt-1">بِواسِطَة: {doc.uploaded_by_name || doc.uploaded_by}</div>
                </div>
                <div className="pt-2 border-t">
                  <div className="text-[10px] text-slate-500">الإصدار الحالي:</div>
                  <div className="font-mono text-[11px]">v{doc.current_version}</div>
                </div>
              </div>
            ) : (
              <div className="space-y-2 text-xs">
                <label className="block">العُنوان:
                  <input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })}
                          data-testid="edit-title"
                          className="w-full rounded border border-slate-300 px-2 py-1 text-xs mt-0.5" />
                </label>
                <label className="block">المَرجِع:
                  <input value={form.reference_no} onChange={(e) => setForm({ ...form, reference_no: e.target.value })}
                          data-testid="edit-ref"
                          className="w-full rounded border border-slate-300 px-2 py-1 text-xs font-mono mt-0.5" />
                </label>
                <label className="block">الطَّرف الآخَر:
                  <input value={form.related_party_name}
                          onChange={(e) => setForm({ ...form, related_party_name: e.target.value })}
                          data-testid="edit-party"
                          className="w-full rounded border border-slate-300 px-2 py-1 text-xs mt-0.5" />
                </label>
                <label className="block">تاريخ المُستَنَد:
                  <input type="date" value={form.document_date}
                          onChange={(e) => setForm({ ...form, document_date: e.target.value })}
                          data-testid="edit-date"
                          className="w-full rounded border border-slate-300 px-2 py-1 text-xs mt-0.5" />
                </label>
                <label className="block">المَبلَغ:
                  <div className="flex items-center gap-1">
                    <input type="number" step="0.01" value={form.amount}
                            onChange={(e) => setForm({ ...form, amount: e.target.value })}
                            data-testid="edit-amount"
                            className="flex-1 rounded border border-slate-300 px-2 py-1 text-xs font-mono mt-0.5" />
                    <input value={form.currency}
                            onChange={(e) => setForm({ ...form, currency: e.target.value })}
                            className="w-12 rounded border border-slate-300 px-2 py-1 text-xs font-mono mt-0.5" />
                  </div>
                </label>
                <label className="block">الوَصف:
                  <textarea value={form.description}
                             onChange={(e) => setForm({ ...form, description: e.target.value })}
                             data-testid="edit-desc"
                             className="w-full rounded border border-slate-300 px-2 py-1 text-xs mt-0.5 h-20 resize-none" />
                </label>
              </div>
            )}

            {/* Workflow transitions */}
            {nextStates.length > 0 && (
              <div className="pt-3 border-t" data-testid="transitions-block">
                <div className="text-[10px] uppercase font-semibold text-slate-500 mb-1">
                  الخُطوَة التالِيَة
                </div>
                <div className="flex flex-wrap gap-1">
                  {nextStates.map((s) => (
                    <button key={s} onClick={() => transition(s)} disabled={busy}
                             data-testid={`transition-${s}`}
                             className="inline-flex items-center gap-1 text-[11px] font-semibold rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white px-2 py-1 disabled:opacity-50">
                      <ArrowRight className="w-3 h-3" />
                      {STATE_AR[s]}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Tags */}
            <div className="pt-3 border-t">
              <div className="text-[10px] uppercase font-semibold text-slate-500 mb-1 flex items-center gap-1">
                <TagIcon className="w-3 h-3" /> الوُسوم
              </div>
              <div className="flex flex-wrap gap-1">
                {(doc.tags || []).map((t) => (
                  <span key={t} className="text-[10px] bg-slate-200 rounded px-1.5 py-0.5">
                    {t}
                  </span>
                ))}
                {!doc.tags?.length && <span className="text-[10px] text-slate-400">لا يُوجَد</span>}
              </div>
            </div>

            {/* Links */}
            {doc.links?.length > 0 && (
              <div className="pt-3 border-t">
                <div className="text-[10px] uppercase font-semibold text-slate-500 mb-1 flex items-center gap-1">
                  <LinkIcon className="w-3 h-3" /> الرَّوابِط
                </div>
                <div className="space-y-1">
                  {doc.links.map((l, i) => (
                    <div key={i} className="text-[11px] font-mono text-slate-700">
                      {l.entity_kind}:<code>{l.entity_id.slice(0, 12)}</code>
                    </div>
                  ))}
                </div>
              </div>
            )}
            </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
